import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { listarCandidatos } from '../api/candidatos';

interface Candidato {
  id: number;
  nome: string;
}

interface CandidatoContextType {
  candidatos: Candidato[]; // Todos os candidatos disponíveis
  candidatosPermitidos: number[]; // IDs dos candidatos que este admin pode acessar (vazio = todos)
  selecionado: number | null; // null = admin sem filtro, number = admin como candidato
  carregando: boolean;
  mudarCandidato: (id: number | null) => void; // null = modo admin completo
  roleEmContexto: string; // role efetivo do usuário no contexto atual
}

const CandidatoContext = createContext<CandidatoContextType | undefined>(undefined);

export function CandidatoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [candidatosPermitidos, setCandidatosPermitidos] = useState<number[]>([]); // IDs que este admin pode acessar
  const [selecionado, setSelecionado] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    // Só carrega se for admin
    if (user?.role !== 'admin' || !user?.id) {
      setCarregando(false);
      setSelecionado(null);
      setCandidatosPermitidos([]);
      return;
    }

    // Carrega todos os candidatos + candidatos permitidos para este admin
    Promise.all([
      listarCandidatos(),
      fetch(`/api/usuarios/${user.id}/acesso-candidatos`)
        .then(r => r.json())
        .catch(() => []) // Se falhar, retorna array vazio (admin sem restrição)
    ])
      .then(([lista, permitidos]) => {
        setCandidatos(lista);
        setCandidatosPermitidos(permitidos || []);

        // Tenta recuperar último selecionado do localStorage
        const salvo = localStorage.getItem('admin_candidato_id');
        if (salvo) {
          const id = Number(salvo);
          // 'admin' = modo admin sem filtro, número = candidato específico
          if (salvo === 'admin' || isNaN(id)) {
            setSelecionado(null); // Modo admin completo
          } else if (lista.some((c: Candidato) => c.id === id)) {
            setSelecionado(id);
          } else {
            setSelecionado(null);
          }
        } else {
          setSelecionado(null); // Default: modo admin sem filtro
        }
      })
      .finally(() => setCarregando(false));
  }, [user?.role, user?.id]);

  const mudarCandidato = (id: number | null) => {
    setSelecionado(id);
    localStorage.setItem('admin_candidato_id', id === null ? 'admin' : String(id));
  };

  // Quando admin, roleEmContexto = 'admin'. Quando não admin, mantém o role original
  const roleEmContexto = user?.role === 'admin' ? 'admin' : user?.role || 'unknown';

  return (
    <CandidatoContext.Provider value={{ candidatos, candidatosPermitidos, selecionado, carregando, mudarCandidato, roleEmContexto }}>
      {children}
    </CandidatoContext.Provider>
  );
}

export function useCandidatoContext() {
  const context = useContext(CandidatoContext);
  if (!context) {
    // Retorna valores default se fora do provider
    return {
      candidatos: [],
      candidatosPermitidos: [],
      selecionado: null,
      carregando: false,
      mudarCandidato: () => {},
      roleEmContexto: 'unknown',
    };
  }
  return context;
}
